// src/pages/Providers.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { Server, Plus, Edit2, Trash2, X, Hash, Network, Search, Upload, Gauge, Undo2 } from 'lucide-react';
import { capacityLimitsService } from 'api/services/capacityLimits.service';
import {
  providersService, Provider, ProviderNumber, BusinessProviderConfig,
  EngineType, RoutingMode, ResolveResult, CapacityMode, NumberStatus, CapacitySummary
} from 'api/services/providers.service';
import { useAuth } from 'hooks/useAuth';
import { useFeatureGrants } from 'hooks/useFeatureGrants';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import {
  isPlatformTier, trunkLabel, mayManageTrunk, manageableTrunks, mayDeleteNumber, mayChangeRouting,
  PLATFORM_CARRIERS_COPY, holdsTelephonyGrant, TRUNKS_GRANT_COPY,
} from './telephonyAccessRules';
import { capacityChanged, reasonError, NOT_YET_ENFORCED_COPY } from './capacityEnforcementRules';

const INPUT = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white';
const ENGINES: EngineType[] = ['ASTERISK', 'FREESWITCH'];
const MODES: RoutingMode[] = ['DIRECT', 'KAMAILIO'];
const CAP_MODES: CapacityMode[] = ['SHARED_POOL', 'PER_ORG'];
const NUM_STATUSES: NumberStatus[] = ['AVAILABLE', 'ALLOCATED', 'SUSPENDED'];

type Tab = 'providers' | 'numbers' | 'routing';

const emptyProvider = (): Provider => ({
  name: '', engine_type: 'ASTERISK', routing_mode: 'DIRECT', region: '',
  control_url: '', sip_endpoint: '',
  capabilities: { supportsRefer: true, supportsDial: true, supportsConference: true },
  cps_limit: null, max_channels: null, capacity_mode: 'SHARED_POOL',
  healthy: true, enabled: true, notes: ''
});

const statusBadge = (s?: NumberStatus) => {
  const cls = s === 'ALLOCATED' ? 'bg-blue-100 text-blue-800'
    : s === 'SUSPENDED' ? 'bg-red-100 text-red-800'
    : 'bg-green-100 text-green-800';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{s || 'AVAILABLE'}</span>;
};

const Providers: React.FC = () => {
  const { currentOrganization, user } = useAuth();
  const role = (currentOrganization?.role || user?.role)?.toLowerCase();
  const canManage = ['master_admin', 'super_admin', 'service_admin', 'platform_admin', 'provider_owner', 'provider_admin'].includes(role || '');
  // Non-master callers see only their own trunks and the numbers their tree holds; the
  // platform's trunks and pool are Green Giraffe's (14 Oct). The server enforces it.
  const isMaster = isPlatformTier(role);
  // Adding/editing/deleting their own trunks and the numbers on them is off for a provider
  // until Green Giraffe grants ADD_PROVIDER (14 Oct). Without it the page is view-only.
  const grants = useFeatureGrants();
  const canManageTrunks = canManage && holdsTelephonyGrant(role, grants, 'ADD_PROVIDER');

  const [tab, setTab] = useState<Tab>('providers');
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const providerName = (id?: number | null, known?: string | null) => trunkLabel(id, providers, isMaster, known);
  const ownTrunks = manageableTrunks(providers, isMaster);

  const loadProviders = useCallback(async () => {
    setLoading(true);
    const res = await providersService.list();
    if (res.success) setProviders(res.data || []);
    else setError(res.error || 'Failed to load providers');
    setLoading(false);
  }, []);

  useEffect(() => { loadProviders(); }, [loadProviders]);

  // ---- Provider modal ----
  const [pModal, setPModal] = useState(false);
  const [pEditing, setPEditing] = useState<Provider | null>(null);
  const [pForm, setPForm] = useState<Provider>(emptyProvider());
  const [pErr, setPErr] = useState<string | null>(null);
  // Required whenever a trunk's cps_limit / max_channels move (capacity history, 14 Oct).
  const [pReason, setPReason] = useState('');
  const [pSaving, setPSaving] = useState(false);
  const [pDeleting, setPDeleting] = useState<number | null>(null);

  // capacity view
  const [capFor, setCapFor] = useState<Provider | null>(null);
  const [capData, setCapData] = useState<CapacitySummary | null>(null);
  const openCapacity = async (p: Provider) => {
    if (!p.id) return;
    setCapFor(p); setCapData(null);
    const res = await providersService.capacity(p.id);
    if (res.success) setCapData(res.data || null);
  };

  // health checks
  const [checking, setChecking] = useState<number | 'all' | null>(null);
  const checkOne = async (p: Provider) => {
    if (!p.id) return;
    setChecking(p.id);
    const r = await providersService.healthCheck(p.id);
    setChecking(null);
    if (r.success) loadProviders();
  };
  const checkAll = async () => {
    setChecking('all');
    const r = await providersService.healthCheckAll();
    setChecking(null);
    if (r.success) loadProviders();
  };
  const relTime = (iso?: string) => {
    if (!iso) return 'never';
    const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    return `${Math.round(s / 3600)}h ago`;
  };
  const healthBadge = (p: Provider) => {
    if (p.last_checked_at == null) return <span className="text-xs text-gray-400 italic">unchecked</span>;
    return p.healthy
      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">UP{p.latency_ms != null ? ` ${p.latency_ms}ms` : ''}</span>
      : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" title={p.health_detail || ''}>DOWN</span>;
  };

  const openProviderCreate = () => { setPEditing(null); setPForm(emptyProvider()); setPErr(null); setPReason(''); setPModal(true); };
  const openProviderEdit = (p: Provider) => { setPEditing(p); setPReason(''); setPForm({ ...emptyProvider(), ...p, capabilities: { ...emptyProvider().capabilities, ...(p.capabilities || {}) } }); setPErr(null); setPModal(true); };

  const pCapacityMoved = capacityChanged(pEditing || { cps_limit: null, max_channels: null }, pForm);
  const saveProvider = async () => {
    setPErr(null);
    if (!pForm.name.trim()) { setPErr('Name is required'); return; }
    const moved = pCapacityMoved;
    if (moved) {
      const rErr = reasonError(pReason);
      if (rErr) { setPErr(rErr); return; }
    }
    setPSaving(true);
    const reason = moved ? pReason.trim() : undefined;
    const res = pEditing?.id
      ? await providersService.update(pEditing.id, pForm, reason)
      : await providersService.create(pForm, reason);
    setPSaving(false);
    if (res.success) { setPModal(false); loadProviders(); }
    else setPErr(res.error || 'Save failed');
  };

  const deleteProvider = async (p: Provider) => {
    if (!p.id || !window.confirm(`Delete provider "${p.name}"?`)) return;
    setPDeleting(p.id);
    const res = await providersService.remove(p.id);
    setPDeleting(null);
    if (res.success) loadProviders();
    else alert(res.error || 'Delete failed');
  };

  // ---- Numbers tab ----
  const [numbers, setNumbers] = useState<ProviderNumber[]>([]);
  const [numFilter, setNumFilter] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<NumberStatus | ''>('');

  const loadNumbers = useCallback(async () => {
    const res = await providersService.listNumbers({
      providerId: numFilter || undefined,
      status: statusFilter || undefined
    });
    if (res.success) setNumbers(res.data || []);
  }, [numFilter, statusFilter]);

  useEffect(() => { if (tab === 'numbers') loadNumbers(); }, [tab, loadNumbers]);

  const deleteNumber = async (n: ProviderNumber) => {
    if (!n.id || !window.confirm(`Delete number ${n.e164}?`)) return;
    const res = await providersService.removeNumber(n.id);
    if (res.success) loadNumbers(); else alert(res.error || 'Delete failed');
  };

  const releaseNumber = async (n: ProviderNumber) => {
    if (!n.id || !window.confirm(`Release ${n.e164} from ${n.business_id} back to the pool?`)) return;
    const res = await providersService.releaseNumber(n.id);
    if (res.success) loadNumbers(); else alert(res.error || 'Release failed');
  };

  // bulk import
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkProvider, setBulkProvider] = useState<number>(0);
  const [bulkText, setBulkText] = useState('');
  const [bulkErr, setBulkErr] = useState<string | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkResult, setBulkResult] = useState<{ created: number; skipped: string[] } | null>(null);

  const runBulkImport = async () => {
    setBulkErr(null); setBulkResult(null);
    if (!bulkProvider) { setBulkErr('Select a provider'); return; }
    const list = bulkText.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
    if (list.length === 0) { setBulkErr('Paste at least one number'); return; }
    setBulkSaving(true);
    const res = await providersService.bulkCreateNumbers(bulkProvider, list);
    setBulkSaving(false);
    if (res.success) { setBulkResult({ created: res.data!.created, skipped: res.data!.skipped }); setBulkText(''); loadNumbers(); }
    else setBulkErr(res.error || 'Import failed');
  };

  // allocate
  const [allocNum, setAllocNum] = useState<ProviderNumber | null>(null);
  const [allocBiz, setAllocBiz] = useState('');
  const [allocErr, setAllocErr] = useState<string | null>(null);
  const [allocSaving, setAllocSaving] = useState(false);

  const runAllocate = async () => {
    if (!allocNum?.id) return;
    setAllocErr(null);
    if (!allocBiz.trim()) { setAllocErr('business_id is required'); return; }
    setAllocSaving(true);
    const res = await providersService.allocateNumber(allocNum.id, allocBiz.trim());
    setAllocSaving(false);
    if (res.success) { setAllocNum(null); setAllocBiz(''); loadNumbers(); }
    else setAllocErr(res.error || 'Allocate failed');
  };

  // bulk select + bulk actions
  const [selectedNums, setSelectedNums] = useState<Set<number>>(new Set());
  const toggleSel = (id?: number) => { if (id == null) return; setSelectedNums(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const allSelected = numbers.length > 0 && numbers.every(n => n.id != null && selectedNums.has(n.id));
  const toggleSelAll = () => setSelectedNums(allSelected ? new Set() : new Set(numbers.map(n => n.id!).filter(Boolean)));
  const [bulkAllocModal, setBulkAllocModal] = useState(false);
  const [bulkBiz, setBulkBiz] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const runBulkAllocate = async () => {
    if (!bulkBiz.trim()) return;
    setBulkBusy(true);
    const res = await providersService.bulkAllocate(Array.from(selectedNums), bulkBiz.trim());
    setBulkBusy(false);
    if (res.success) { setBulkAllocModal(false); setBulkBiz(''); setSelectedNums(new Set()); loadNumbers(); }
    else alert(res.error || 'Bulk allocate failed');
  };
  const runBulkRelease = async () => {
    if (!window.confirm(`Release ${selectedNums.size} number(s) to the pool?`)) return;
    const res = await providersService.bulkRelease(Array.from(selectedNums));
    if (res.success) { setSelectedNums(new Set()); loadNumbers(); } else alert(res.error || 'Bulk release failed');
  };

  // provider enable/disable (maintenance) toggle
  const toggleProvider = async (p: Provider) => {
    if (!p.id) return;
    const res = await providersService.update(p.id, { enabled: !p.enabled });
    if (res.success) loadProviders(); else alert(res.error || 'Update failed');
  };

  // ---- Routing tab ----
  const [configs, setConfigs] = useState<BusinessProviderConfig[]>([]);
  const [cModal, setCModal] = useState(false);
  const [cForm, setCForm] = useState<BusinessProviderConfig>({ business_id: '', provider_id: 0, priority: 100, routing_mode_override: null, enabled: true });
  // What the org's trunk holds and what is already given out, so a slice is set
  // as a share of something known rather than typed into an empty box.
  const [alloc, setAlloc] = useState<any>(null);
  const loadAllocation = async (businessId: string) => {
    setAlloc(null);
    const id = businessId.trim();
    if (!id) return;
    const res = await capacityLimitsService.allocation(id);
    if (res?.success) setAlloc(res.data);
  };
  const [cErr, setCErr] = useState<string | null>(null);
  const [cReason, setCReason] = useState('');
  const [cSaving, setCSaving] = useState(false);
  const [resolveBiz, setResolveBiz] = useState('');
  const [resolveRes, setResolveRes] = useState<ResolveResult | null>(null);
  const [resolveErr, setResolveErr] = useState<string | null>(null);

  const loadConfigs = useCallback(async () => {
    const res = await providersService.listBusinessConfigs();
    if (res.success) setConfigs(res.data || []);
  }, []);

  useEffect(() => { if (tab === 'routing') loadConfigs(); }, [tab, loadConfigs]);

  const cExisting = configs.find(c => c.business_id === cForm.business_id.trim() && c.provider_id === cForm.provider_id);
  const cCapacityMoved = capacityChanged(cExisting || { cps_limit: null, max_channels: null }, cForm);
  const saveConfig = async () => {
    setCErr(null);
    if (!cForm.business_id.trim()) { setCErr('business_id is required'); return; }
    if (!cForm.provider_id) { setCErr('Select a provider'); return; }
    const moved = cCapacityMoved;
    if (moved) {
      const rErr = reasonError(cReason);
      if (rErr) { setCErr(rErr); return; }
    }
    setCSaving(true);
    const res = await providersService.upsertBusinessConfig(cForm, moved ? cReason.trim() : undefined);
    setCSaving(false);
    if (res.success) { setCModal(false); loadConfigs(); }
    else setCErr(res.error || 'Save failed');
  };

  const deleteConfig = async (c: BusinessProviderConfig) => {
    if (!c.id) return;
    const hasCapacity = c.cps_limit != null || c.max_channels != null;
    let reason: string | undefined;
    if (hasCapacity) {
      const r = window.prompt(`Remove routing for ${c.business_id} → ${providerName(c.provider_id, c.provider_name)}?\n\nIt carries a capacity slice, so give a reason (kept in the capacity history):`);
      if (r === null) return;
      const rErr = reasonError(r);
      if (rErr) { alert(rErr); return; }
      reason = r.trim();
    } else if (!window.confirm(`Remove routing for ${c.business_id} → ${providerName(c.provider_id, c.provider_name)}?`)) return;
    const res = await providersService.removeBusinessConfig(c.id, reason);
    if (res.success) loadConfigs(); else alert(res.error || 'Delete failed');
  };

  const runResolve = async () => {
    setResolveErr(null); setResolveRes(null);
    if (!resolveBiz.trim()) return;
    const res = await providersService.resolve(resolveBiz.trim());
    if (res.success) setResolveRes(res.data || null);
    else setResolveErr(res.error || 'No usable provider');
  };

  const engineBadge = (e?: EngineType) => (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${e === 'FREESWITCH' ? 'bg-orange-100 text-orange-800' : 'bg-purple-100 text-purple-800'}`}>{e}</span>
  );
  const modeBadge = (m?: RoutingMode | null) => m
    ? <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${m === 'KAMAILIO' ? 'bg-amber-100 text-amber-800' : 'bg-sky-100 text-sky-800'}`}>{m}</span>
    : <span className="text-xs text-gray-400 italic">inherit</span>;

  const caps = (c?: Provider['capabilities']) => {
    const on = Object.entries(c || {}).filter(([, v]) => v).map(([k]) => k.replace('supports', ''));
    return on.length ? on.join(', ') : '—';
  };

  if (loading && providers.length === 0) return <LoadingSpinner />;

  const TABS: { key: Tab; label: string; icon: any }[] = [
    { key: 'providers', label: 'Providers', icon: Server },
    { key: 'numbers', label: 'Numbers', icon: Hash },
    { key: 'routing', label: 'Business Routing', icon: Network }
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Providers</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Media-engine registry (engine, region, capabilities, routing mode) — the control plane's source of truth.
          {!canManage && <span className="ml-2 text-xs text-orange-600">(Read-only)</span>}
        </p>
        {canManage && !canManageTrunks && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{TRUNKS_GRANT_COPY}</p>
        )}
      </div>

      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${tab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>}

      {/* PROVIDERS TAB */}
      {tab === 'providers' && (
        <div className="space-y-3">
          <div className="flex justify-end gap-2">
            {isMaster && (
            <button onClick={checkAll} disabled={checking === 'all'}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50">
              {checking === 'all' ? <div className="w-4 h-4 animate-spin rounded-full border-2 border-gray-500 border-t-transparent" /> : <Gauge className="w-4 h-4" />}
              Check health
            </button>
            )}
            {canManageTrunks && (
              <button onClick={openProviderCreate} className="tl-btn tl-btn-primary">
                <Plus className="w-5 h-5" /> Add Provider
              </button>
            )}
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>{['Name', 'Engine', 'Routing', 'Region', 'Health', 'Capacity', 'Capabilities', 'Status', 'Actions'].map(h => (
                    <th key={h} className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${h === 'Actions' ? 'text-center' : 'text-left'}`}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {providers.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-gray-500">{isMaster ? 'No providers configured yet.' : PLATFORM_CARRIERS_COPY}</td></tr>}
                  {providers.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3"><div className="flex items-center gap-2"><Server className="w-4 h-4 text-blue-600" /><span className="font-medium text-gray-900 dark:text-white">{p.name}</span></div></td>
                      <td className="px-4 py-3">{engineBadge(p.engine_type)}</td>
                      <td className="px-4 py-3">{modeBadge(p.routing_mode)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{p.region || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {healthBadge(p)}
                          {canManageTrunks && mayManageTrunk(p, isMaster) && (
                          <button onClick={() => checkOne(p)} disabled={checking === p.id}
                            className="text-xs text-blue-600 hover:underline disabled:opacity-50" title="Probe control_url now">
                            {checking === p.id ? '…' : 'Check'}
                          </button>
                          )}
                        </div>
                        {p.last_checked_at && <p className="text-[11px] text-gray-400 mt-0.5">{relTime(p.last_checked_at)}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => openCapacity(p)} className="inline-flex items-center gap-1 text-xs text-gray-700 dark:text-gray-300 hover:text-blue-600" title="View capacity & inventory">
                          <Gauge className="w-3.5 h-3.5" />
                          {p.cps_limit != null || p.max_channels != null
                            ? <>CPS {p.cps_limit ?? '∞'} · Ch {p.max_channels ?? '∞'}</>
                            : <span className="text-gray-400">unset</span>}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">{caps(p.capabilities)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1 items-start">
                          {canManageTrunks && mayManageTrunk(p, isMaster)
                            ? <button onClick={() => toggleProvider(p)} title={p.enabled ? 'Disable (maintenance)' : 'Enable'}
                                className={`inline-flex w-fit items-center px-2 py-0.5 rounded-full text-xs hover:ring-2 hover:ring-offset-1 ${p.enabled ? 'bg-green-100 text-green-800 hover:ring-green-300' : 'bg-gray-200 text-gray-600 hover:ring-gray-300'}`}>{p.enabled ? 'Enabled' : 'Disabled'}</button>
                            : <span className={`inline-flex w-fit items-center px-2 py-0.5 rounded-full text-xs ${p.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>{p.enabled ? 'Enabled' : 'Disabled'}</span>}
                          <span className={`inline-flex w-fit items-center px-2 py-0.5 rounded-full text-xs ${p.healthy ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>{p.healthy ? 'Healthy' : 'Unhealthy'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {canManageTrunks && mayManageTrunk(p, isMaster) ? (<>
                            <button onClick={() => openProviderEdit(p)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit"><Edit2 className="w-4 h-4" /></button>
                            <button onClick={() => deleteProvider(p)} disabled={pDeleting === p.id} className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50" title="Delete">
                              {pDeleting === p.id ? <div className="w-4 h-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent" /> : <Trash2 className="w-4 h-4" />}
                            </button>
                          </>) : <span className="text-xs text-gray-400 italic" title={isMaster ? undefined : mayManageTrunk(p, isMaster) ? TRUNKS_GRANT_COPY : 'Run by the platform; managed by Green Giraffe'}>View only</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* NUMBERS TAB */}
      {tab === 'numbers' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <select value={numFilter} onChange={(e) => setNumFilter(e.target.value ? Number(e.target.value) : '')} className={`${INPUT} md:w-56`}>
                <option value="">All providers</option>
                {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <select value={statusFilter} onChange={(e) => setStatusFilter((e.target.value || '') as NumberStatus | '')} className={`${INPUT} md:w-44`}>
                <option value="">All statuses</option>
                {NUM_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {canManageTrunks && ownTrunks.length > 0 && (
              <button onClick={() => { setBulkProvider((numFilter && ownTrunks.some(p => p.id === numFilter) ? numFilter : 0) || ownTrunks[0]?.id || 0); setBulkText(''); setBulkErr(null); setBulkResult(null); setBulkModal(true); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 whitespace-nowrap"><Upload className="w-5 h-5" /> Bulk Import</button>
            )}
          </div>
          {canManage && isMaster && selectedNums.size > 0 && (
            <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2">
              <span className="text-sm font-medium text-blue-800 dark:text-blue-200">{selectedNums.size} selected</span>
              <button onClick={() => { setBulkBiz(''); setBulkAllocModal(true); }} className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">Allocate to…</button>
              <button onClick={runBulkRelease} className="px-3 py-1 text-sm text-amber-700 bg-amber-50 hover:bg-amber-100 rounded inline-flex items-center gap-1"><Undo2 className="w-3 h-3" /> Release</button>
              <button onClick={() => setSelectedNums(new Set())} className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">Clear</button>
            </div>
          )}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  {canManage && isMaster && <th className="px-4 py-3 w-8"><input type="checkbox" checked={allSelected} onChange={toggleSelAll} className="w-4 h-4 rounded border-gray-300 text-blue-600" /></th>}
                  {['Number', 'Provider', 'Status', 'Allocated to', 'Actions'].map(h => (
                  <th key={h} className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${h === 'Actions' ? 'text-center' : 'text-left'}`}>{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {numbers.length === 0 && <tr><td colSpan={canManage && isMaster ? 6 : 5} className="px-4 py-10 text-center text-sm text-gray-500">{isMaster
                  ? <>No numbers in inventory{statusFilter ? ` with status ${statusFilter}` : ''}. Use Bulk Import to feed purchased blocks.</>
                  : <>Your organization holds no numbers{statusFilter ? ` with status ${statusFilter}` : ''}. {PLATFORM_CARRIERS_COPY}</>}</td></tr>}
                {numbers.map(n => (
                  <tr key={n.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${n.id != null && selectedNums.has(n.id) ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}>
                    {canManage && isMaster && <td className="px-4 py-3"><input type="checkbox" checked={n.id != null && selectedNums.has(n.id)} onChange={() => toggleSel(n.id)} className="w-4 h-4 rounded border-gray-300 text-blue-600" /></td>}
                    <td className="px-4 py-3 font-mono text-sm text-gray-900 dark:text-white">{n.e164}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{providerName(n.provider_id)}</td>
                    <td className="px-4 py-3">{statusBadge(n.status)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{n.business_id || <span className="text-gray-400">— pool —</span>}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        {canManage && isMaster ? (<>
                          {n.status !== 'ALLOCATED' && (
                            <button onClick={() => { setAllocNum(n); setAllocBiz(''); setAllocErr(null); }} className="px-2 py-1 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 rounded" title="Allocate to a business">Allocate</button>
                          )}
                          {n.status === 'ALLOCATED' && (
                            <button onClick={() => releaseNumber(n)} className="px-2 py-1 text-xs text-amber-700 bg-amber-50 hover:bg-amber-100 rounded inline-flex items-center gap-1" title="Release to pool"><Undo2 className="w-3 h-3" /> Release</button>
                          )}
                          <button onClick={() => deleteNumber(n)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 className="w-4 h-4" /></button>
                        </>) : canManageTrunks && mayDeleteNumber(n, providers, isMaster)
                          ? <button onClick={() => deleteNumber(n)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 className="w-4 h-4" /></button>
                          : <span className="text-xs text-gray-400 italic">—</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ROUTING TAB */}
      {tab === 'routing' && (
        <div className="space-y-4">
          {/* resolve tester */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Resolve tester — which engine does a business route to?</p>
            <div className="flex items-center gap-2">
              <div className="relative md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input value={resolveBiz} onChange={(e) => setResolveBiz(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && runResolve()}
                  placeholder="business_id (e.g. PersonalAssist)" className={`${INPUT} pl-9`} />
              </div>
              <button onClick={runResolve} className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900">Resolve</button>
            </div>
            {resolveRes && (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                <span className="text-gray-500">→</span>
                <span className="font-medium text-gray-900 dark:text-white">{resolveRes.provider}</span>
                {engineBadge(resolveRes.engine)} {modeBadge(resolveRes.routingMode)}
                <span className="text-xs text-gray-500">caps: {caps(resolveRes.capabilities)}</span>
              </div>
            )}
            {resolveErr && <p className="mt-3 text-sm text-red-600">{resolveErr}</p>}
          </div>

          <div className="flex justify-end">
            {canManage && providers.length > 0 && (
              <button onClick={() => { setCForm({ business_id: '', provider_id: providers[0]?.id || 0, priority: 100, routing_mode_override: null, enabled: true }); setCErr(null); setCReason(''); setCModal(true); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"><Plus className="w-5 h-5" /> Add Routing</button>
            )}
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <tr>{['Business', 'Provider', 'Priority', 'Routing override', 'Caps (CPS/Ch)', 'Status', 'Actions'].map(h => (
                  <th key={h} className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${h === 'Actions' ? 'text-center' : 'text-left'}`}>{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {configs.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">No business routing configured.</td></tr>}
                {configs.sort((a, b) => (a.priority || 0) - (b.priority || 0)).map(c => (
                  <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{c.business_id}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{providerName(c.provider_id, c.provider_name)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{c.priority}</td>
                    <td className="px-4 py-3">{modeBadge(c.routing_mode_override)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{(c.cps_limit ?? null) === null && (c.max_channels ?? null) === null ? <span className="text-gray-400">pool</span> : <>{c.cps_limit ?? '∞'} / {c.max_channels ?? '∞'}</>}</td>
                    <td className="px-4 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${c.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>{c.enabled ? 'Enabled' : 'Disabled'}</span></td>
                    <td className="px-4 py-3 text-center">
                      {canManage && mayChangeRouting(c, isMaster) ? <button onClick={() => deleteConfig(c)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 className="w-4 h-4" /></button> : <span className="text-xs text-gray-400 italic" title={c.platform_managed ? PLATFORM_CARRIERS_COPY : undefined}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* PROVIDER MODAL */}
      {pModal && (
        <Modal title={pEditing ? 'Edit Provider' : 'Add Provider'} onClose={() => setPModal(false)} onSave={saveProvider} saving={pSaving} saveLabel={pEditing ? 'Save Changes' : 'Create'}>
          {pErr && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{pErr}</div>}
          <Field label="Name"><input value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} className={INPUT} placeholder="e.g. tata1-landline" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Engine">
              <select value={pForm.engine_type} onChange={(e) => setPForm({ ...pForm, engine_type: e.target.value as EngineType })} className={INPUT}>
                {ENGINES.map(x => <option key={x} value={x}>{x}</option>)}
              </select>
            </Field>
            <Field label="Routing mode">
              <select value={pForm.routing_mode} onChange={(e) => setPForm({ ...pForm, routing_mode: e.target.value as RoutingMode })} className={INPUT}>
                {MODES.map(x => <option key={x} value={x}>{x}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Region"><input value={pForm.region || ''} onChange={(e) => setPForm({ ...pForm, region: e.target.value })} className={INPUT} placeholder="e.g. in" /></Field>
            <Field label="SIP endpoint"><input value={pForm.sip_endpoint || ''} onChange={(e) => setPForm({ ...pForm, sip_endpoint: e.target.value })} className={`${INPUT} font-mono text-sm`} placeholder="PJSIP endpoint" /></Field>
          </div>
          <Field label="Control URL (voiceflow origination edge / ARI-AMI adapter)">
            <input value={pForm.control_url || ''} onChange={(e) => setPForm({ ...pForm, control_url: e.target.value })} className={`${INPUT} font-mono text-sm`} placeholder="http://host:3001" />
          </Field>
          <Field label="Throttle URL (where origination enforcement runs; blank = this cpass)">
            <input value={pForm.throttle_url || ''} onChange={(e) => setPForm({ ...pForm, throttle_url: e.target.value })} className={`${INPUT} font-mono text-sm`} placeholder="http://cpass-host/api/telephony/throttle" />
          </Field>

          {/* Upstream carrier SIP trunk */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-300">Carrier SIP trunk (upstream PSTN — e.g. Tata)</p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Trunk host / IP"><input value={pForm.trunk_host || ''} onChange={(e) => setPForm({ ...pForm, trunk_host: e.target.value })} className={`${INPUT} font-mono text-sm`} placeholder="sip.carrier.example" /></Field>
              <Field label="Port"><input type="number" value={pForm.trunk_port ?? ''} onChange={(e) => setPForm({ ...pForm, trunk_port: e.target.value ? Number(e.target.value) : null })} className={INPUT} placeholder="5060" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Transport">
                <select value={pForm.trunk_transport || ''} onChange={(e) => setPForm({ ...pForm, trunk_transport: e.target.value })} className={INPUT}>
                  <option value="">—</option><option value="UDP">UDP</option><option value="TCP">TCP</option><option value="TLS">TLS</option>
                </select>
              </Field>
              <Field label="Auth mode">
                <select value={pForm.trunk_auth_mode || ''} onChange={(e) => setPForm({ ...pForm, trunk_auth_mode: e.target.value })} className={INPUT}>
                  <option value="">—</option><option value="IP_WHITELIST">IP whitelist (no creds)</option><option value="REGISTER">Register (user/pass)</option>
                </select>
              </Field>
            </div>
            {pForm.trunk_auth_mode === 'REGISTER' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Username"><input value={pForm.trunk_username || ''} onChange={(e) => setPForm({ ...pForm, trunk_username: e.target.value })} className={INPUT} placeholder="trunk user" /></Field>
                  <Field label="Password"><input type="password" value={pForm.trunk_password || ''} onChange={(e) => setPForm({ ...pForm, trunk_password: e.target.value })} className={INPUT} placeholder={pForm.trunk_password_set ? '•••••• set — blank to keep' : 'registration password'} /></Field>
                </div>
                <Field label="Realm / domain"><input value={pForm.trunk_realm || ''} onChange={(e) => setPForm({ ...pForm, trunk_realm: e.target.value })} className={`${INPUT} font-mono text-sm`} placeholder="carrier realm (often the host)" /></Field>
              </>
            )}
          </div>

          <Field label="Capabilities">
            <div className="flex flex-wrap gap-4">
              {(['supportsRefer', 'supportsDial', 'supportsConference'] as const).map(k => (
                <label key={k} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={!!pForm.capabilities?.[k]} onChange={(e) => setPForm({ ...pForm, capabilities: { ...pForm.capabilities, [k]: e.target.checked } })} className="w-4 h-4 rounded border-gray-300 text-blue-600" />
                  {k.replace('supports', '')}
                </label>
              ))}
            </div>
          </Field>
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Capacity granted by this provider</p>
            {!isMaster && <p className="text-xs text-gray-500 mb-2">A trunk's channels and CPS are set by the platform admin.</p>}
            <div className="grid grid-cols-3 gap-4">
              <Field label="CPS limit">
                <input type="number" min={0} disabled={!isMaster} value={pForm.cps_limit ?? ''} onChange={(e) => setPForm({ ...pForm, cps_limit: e.target.value === '' ? null : Number(e.target.value) })} className={INPUT} placeholder="unset" />
              </Field>
              <Field label="Max channels">
                <input type="number" min={0} disabled={!isMaster} value={pForm.max_channels ?? ''} onChange={(e) => setPForm({ ...pForm, max_channels: e.target.value === '' ? null : Number(e.target.value) })} className={INPUT} placeholder="unset" />
              </Field>
              <Field label="Capacity mode">
                <select value={pForm.capacity_mode} onChange={(e) => setPForm({ ...pForm, capacity_mode: e.target.value as CapacityMode })} className={INPUT}>
                  {CAP_MODES.map(x => <option key={x} value={x}>{x === 'SHARED_POOL' ? 'Shared pool' : 'Per org'}</option>)}
                </select>
              </Field>
            </div>
            {pCapacityMoved && (
              <div className="mt-3">
                <Field label="Reason for the capacity change (required)">
                  <textarea value={pReason} onChange={(e) => setPReason(e.target.value)} rows={2} className={INPUT}
                    placeholder="e.g. Tata contract: 40 channels on the mobile trunk" />
                </Field>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">{NOT_YET_ENFORCED_COPY}</p>
              </div>
            )}
          </div>
          <Field label="Notes"><input value={pForm.notes || ''} onChange={(e) => setPForm({ ...pForm, notes: e.target.value })} className={INPUT} /></Field>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300"><input type="checkbox" checked={!!pForm.enabled} onChange={(e) => setPForm({ ...pForm, enabled: e.target.checked })} className="w-4 h-4 rounded border-gray-300 text-blue-600" /> Enabled</label>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300"><input type="checkbox" checked={!!pForm.healthy} onChange={(e) => setPForm({ ...pForm, healthy: e.target.checked })} className="w-4 h-4 rounded border-gray-300 text-blue-600" /> Healthy</label>
          </div>
        </Modal>
      )}

      {/* BULK IMPORT MODAL */}
      {bulkModal && (
        <Modal title="Bulk Import Numbers" onClose={() => setBulkModal(false)} onSave={runBulkImport} saving={bulkSaving} saveLabel="Import">
          {bulkErr && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{bulkErr}</div>}
          {bulkResult && (
            <div className="bg-green-50 border border-green-200 text-green-800 text-sm rounded-lg px-3 py-2">
              Imported {bulkResult.created} number(s){bulkResult.skipped.length > 0 && <>, skipped {bulkResult.skipped.length} already-registered: <span className="font-mono text-xs">{bulkResult.skipped.slice(0, 5).join(', ')}{bulkResult.skipped.length > 5 ? '…' : ''}</span></>}.
            </div>
          )}
          <Field label="Provider (carrier the block was purchased from)">
            <select value={bulkProvider} onChange={(e) => setBulkProvider(Number(e.target.value))} className={INPUT}>
              <option value={0}>— select —</option>
              {ownTrunks.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <Field label="Numbers (one per line, or comma/space separated)">
            <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={8}
              placeholder={'+912265197300\n+912265197301\n+912265197302'}
              className={`${INPUT} font-mono text-sm`} />
            <p className="text-xs text-gray-400 mt-1">Imported as AVAILABLE into the pool; duplicates are skipped. Allocate to organizations afterward.</p>
          </Field>
        </Modal>
      )}

      {/* ALLOCATE MODAL */}
      {allocNum && (
        <Modal title={`Allocate ${allocNum.e164}`} onClose={() => setAllocNum(null)} onSave={runAllocate} saving={allocSaving} saveLabel="Allocate">
          {allocErr && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{allocErr}</div>}
          <p className="text-sm text-gray-600 dark:text-gray-400">Consume this number from the {providerName(allocNum.provider_id)} pool and assign it to a business.</p>
          <Field label="Business ID">
            <input value={allocBiz} onChange={(e) => setAllocBiz(e.target.value)} className={INPUT} placeholder="e.g. PersonalAssist" autoFocus />
          </Field>
        </Modal>
      )}

      {/* BULK ALLOCATE MODAL */}
      {bulkAllocModal && (
        <Modal title={`Allocate ${selectedNums.size} number(s)`} onClose={() => setBulkAllocModal(false)} onSave={runBulkAllocate} saving={bulkBusy} saveLabel="Allocate">
          <p className="text-sm text-gray-600 dark:text-gray-400">Assign the {selectedNums.size} selected number(s) to a business (reassigns any already allocated).</p>
          <Field label="Business ID">
            <input value={bulkBiz} onChange={(e) => setBulkBiz(e.target.value)} className={INPUT} placeholder="e.g. PersonalAssist" autoFocus />
          </Field>
        </Modal>
      )}

      {/* CAPACITY MODAL */}
      {capFor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setCapFor(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"><Gauge className="w-5 h-5 text-blue-600" /> {capFor.name} — capacity</h2>
              <button onClick={() => setCapFor(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-4">
              {!capData ? <LoadingSpinner /> : (
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Mode</span><span className="font-medium">{capData.capacityMode}</span></div>
                  <CapRow label="CPS" total={capData.cpsLimit} used={capData.cpsAllocatedToOrgs} remaining={capData.cpsRemaining} />
                  <CapRow label="Channels" total={capData.maxChannels} used={capData.channelsAllocatedToOrgs} remaining={capData.channelsRemaining} />
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-3 grid grid-cols-3 gap-2 text-center">
                    <div><div className="text-lg font-semibold text-gray-900 dark:text-white">{capData.numbersTotal}</div><div className="text-xs text-gray-500">numbers</div></div>
                    <div><div className="text-lg font-semibold text-green-600">{capData.numbersAvailable}</div><div className="text-xs text-gray-500">available</div></div>
                    <div><div className="text-lg font-semibold text-blue-600">{capData.numbersAllocated}</div><div className="text-xs text-gray-500">allocated</div></div>
                  </div>
                  <p className="text-xs text-gray-400">{capData.orgSlices} org capacity slice(s) configured.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ROUTING MODAL */}
      {cModal && (
        <Modal title="Add Business Routing" onClose={() => setCModal(false)} onSave={saveConfig} saving={cSaving} saveLabel="Save">
          {cErr && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{cErr}</div>}
          <Field label="Business ID"><input value={cForm.business_id} onChange={(e) => setCForm({ ...cForm, business_id: e.target.value })} onBlur={(e) => loadAllocation(e.target.value)} className={INPUT} placeholder="e.g. PersonalAssist" /></Field>
          <Field label="Provider">
            <select value={cForm.provider_id ?? 0} onChange={(e) => setCForm({ ...cForm, provider_id: Number(e.target.value) })} className={INPUT}>
              <option value={0}>— select —</option>
              {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Priority (lower = preferred)"><input type="number" value={cForm.priority} onChange={(e) => setCForm({ ...cForm, priority: Number(e.target.value) })} className={INPUT} /></Field>
            <Field label="Routing override">
              <select value={cForm.routing_mode_override || ''} onChange={(e) => setCForm({ ...cForm, routing_mode_override: (e.target.value || null) as RoutingMode | null })} className={INPUT}>
                <option value="">(inherit from provider)</option>
                {MODES.map(x => <option key={x} value={x}>{x}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {alloc && (
              <div className="col-span-2 rounded border px-3 py-2 text-xs
                              border-gray-200 bg-gray-50 text-gray-700
                              dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
                {(() => {
                  // The ceiling belongs to the trunk this org's OUTBOUND leaves on,
                  // which is not necessarily the provider selected above — that
                  // dropdown sets a routing preference, this describes where the
                  // calls go today. Saying "trunk 2" left the reader to work out
                  // which carrier that is; the name is what they know it by.
                  const trunkId = alloc.trunk == null ? undefined : Number(alloc.trunk);
                  const trunkLabel = alloc.platformManaged ? 'Platform'
                    : trunkId == null || Number.isNaN(trunkId)
                    ? String(alloc.trunk ?? '—') : providerName(trunkId);
                  const differs = trunkId != null && !Number.isNaN(trunkId)
                    && (cForm.provider_id ?? 0) > 0 && cForm.provider_id !== trunkId;
                  return alloc.trunkMaxChannels == null ? (
                    <>No channel ceiling is recorded for this organization's outbound trunk, so any slice is accepted.</>
                  ) : (
                    <>
                      Outbound for this org leaves on{' '}
                      <span className="font-medium">{trunkLabel}</span>
                      {alloc.trunkSource === 'assumed-from-numbers' && (
                        // Say so rather than imply certainty: with no outbound route
                        // configured this is the trunk most of the org's numbers sit
                        // on, which is a guess — a good one, but a guess.
                        <span className="ml-1 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          assumed — no outbound route set
                        </span>
                      )}
                      , which holds{' '}
                      <span className="font-medium">{alloc.trunkMaxChannels}</span> channels
                      {alloc.allocatedAcrossOrgs != null && (
                        <> ·{' '}<span className="font-medium">{alloc.allocatedAcrossOrgs}</span> allocated across orgs ·{' '}
                        <span className="font-medium">{alloc.unallocated}</span> unallocated</>
                      )}
                      {alloc.oversubscribed && (
                        // Reported, never enforced: tenants do not all peak together,
                        // and the total counts allocations for organizations that may
                        // themselves be disabled.
                        <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                          oversubscribed — allowed, not blocked
                        </span>
                      )}
                      {alloc.thisOrgMaxChannels != null && (
                        <> · this org currently has <span className="font-medium">{alloc.thisOrgMaxChannels}</span></>
                      )}
                      {differs && (
                        // Without this the two numbers read as one statement: a
                        // ceiling from one trunk sitting directly above a slice
                        // being set on another.
                        <div className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">
                          Note: the slice below applies to <span className="font-medium">{providerName(cForm.provider_id)}</span>,
                          which is not the trunk above. The ceiling shown is {trunkLabel}'s.
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
            <Field label="CPS slice for this org"><input type="number" min={0} value={cForm.cps_limit ?? ''} onChange={(e) => setCForm({ ...cForm, cps_limit: e.target.value === '' ? null : Number(e.target.value) })} className={INPUT} placeholder="from pool" /></Field>
            <Field label="Channel slice for this org"><input type="number" min={0} value={cForm.max_channels ?? ''} onChange={(e) => setCForm({ ...cForm, max_channels: e.target.value === '' ? null : Number(e.target.value) })} className={INPUT} placeholder="from pool" /></Field>
          </div>
          {cCapacityMoved && (
            <Field label="Reason for the capacity change (required)">
              <textarea value={cReason} onChange={(e) => setCReason(e.target.value)} rows={2} className={INPUT}
                placeholder="Why this slice? Kept in the capacity history with your name." />
            </Field>
          )}
          <p className="text-xs text-gray-400">Leave capacity blank to draw from the provider's shared pool. Caps apply when the provider is in Per-org mode.</p>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300"><input type="checkbox" checked={!!cForm.enabled} onChange={(e) => setCForm({ ...cForm, enabled: e.target.checked })} className="w-4 h-4 rounded border-gray-300 text-blue-600" /> Enabled</label>
        </Modal>
      )}
    </div>
  );
};

// --- small modal + field helpers ---
const Modal: React.FC<{ title: string; onClose: () => void; onSave: () => void; saving: boolean; saveLabel: string; children: React.ReactNode }> =
  ({ title, onClose, onSave, saving, saveLabel, children }) => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-4 space-y-4">{children}</div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
          <button onClick={onSave} disabled={saving} className="tl-btn tl-btn-primary disabled:opacity-50">
            {saving && <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />}{saveLabel}
          </button>
        </div>
      </div>
    </div>
  );

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
    {children}
  </div>
);

const CapRow: React.FC<{ label: string; total: number | null; used: number; remaining: number | null }> = ({ label, total, used, remaining }) => (
  <div>
    <div className="flex justify-between mb-1">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium">{total == null ? 'unlimited' : `${used} / ${total} used`}</span>
    </div>
    {total != null && total > 0 && (
      <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className="h-full bg-blue-500" style={{ width: `${Math.min(100, Math.round((used / total) * 100))}%` }} />
      </div>
    )}
    {total != null && <p className="text-xs text-gray-400 mt-0.5">{remaining} remaining</p>}
  </div>
);

export default Providers;
